// components/InsightsDrawer/InsightsDrawer.jsx
import React, { useState, useEffect } from "react";
import {
  Drawer,
  Tabs,
  Card,
  Button,
  Tooltip,
  Space,
  Typography,
  Segmented,
  Table,
  Tag,
  Select,
  Spin,
  Slider,
} from "antd";
import {
  EnvironmentOutlined,
  StarOutlined,
  LinkOutlined,
  LineChartOutlined,
  AppstoreOutlined,
  DollarOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import ActiveCompsTab from "./ActiveCompsTab";
import SoldCompsTab from "./SoldCompsTab";
import { useOverview } from "../../hooks/useOverview";
import Overview from "../../../SearchInsights/components/Overview/Overview";
import MarketAnalysis from "../../../SearchInsights/components/MarketAnalysis/MarketAnalysis";
import ActiveCompsContent from "../../../SearchInsights/components/ActiveCompsContent/ActiveCompsContent";
import SoldCompsContent from "../../../SearchInsights/components/SoldCompsContent/SoldCompsContent";
import { fetchUserDetails } from "../../../Dashboard/services/api";

const { Title, Text } = Typography;

/* -------------------- helpers -------------------- */
const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const fmtDateWithTime = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  const dateStr = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  const timeStr = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  return `${dateStr} ${timeStr}`;
};
const fmtUSD = (n) =>
  typeof n === "number" && isFinite(n)
    ? n.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })
    : "—";

/* -------------------- tiny bits -------------------- */

function Pill({ children, tone = "green" }) {
  const T = {
    green: { bg: "rgba(16, 185, 129, 0.2)", bd: "#10b981", fg: "#34d399" },
    blue: { bg: "rgba(59, 130, 246, 0.2)", bd: "#3b82f6", fg: "#60a5fa" },
    gray: { bg: "rgba(107, 114, 128, 0.2)", bd: "#6b7280", fg: "#9ca3af" },
  }[tone];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 10px",
        borderRadius: 999,
        border: `1px solid ${T.bd}`,
        background: T.bg,
        color: T.fg,
        fontWeight: 700,
        fontSize: 12,
        lineHeight: 1,
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {children}
    </span>
  );
}

function GradeBadge({ grade = "A+", size = 36 }) {
  return (
    <div
      aria-label={`Score ${grade}`}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: "50%",
        background: "#141726",
        color: "#ffffff",
        display: "grid",
        placeItems: "center",
        fontWeight: 800,
        lineHeight: 1,
        boxShadow: "0 0 0 3px #22d3ee inset, 0 0 20px rgba(34, 211, 238, 0.3)",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.05)";
        e.currentTarget.style.boxShadow = "0 0 0 3px #22d3ee inset, 0 0 20px rgba(34, 211, 238, 0.3), 0 8px 32px rgba(0, 0, 0, 0.3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 0 0 3px #22d3ee inset, 0 0 20px rgba(34, 211, 238, 0.3)";
      }}
    >
      <span style={{ fontSize: Math.round(size * 0.48), textShadow: "0 2px 10px rgba(34, 211, 238, 0.3)" }}>
        {grade}
      </span>
    </div>
  );
}

function PriceRail({ asking, retail, profit, middleLabel = "Retail" }) {
  const min = asking;
  const max = profit;
  const pct = (v) => ((v - min) / (max - min || 1)) * 100;

  const Dot = ({ left, label, value, color }) => (
    <div style={{ position: "absolute", left: `calc(${left}% - 8px)`, top: -6 }}>
      <Tooltip
        title={
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#8ea0c6" }}>{label}</div>
            <div style={{ fontWeight: 700 }}>{fmtUSD(value)}</div>
          </div>
        }
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            background: "#0b1220",
            border: `3px solid ${color}`,
            boxShadow: "0 2px 6px rgba(0,0,0,.55)",
          }}
        />
      </Tooltip>
    </div>
  );

  return (
    <div style={{ padding: "6px 0 20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
          color: "#9aa6b2",
          fontSize: 12,
          padding: "0 2px",
        }}
      >
        <div style={{ width: 110, textAlign: "left" }}>
          <div>Asking</div>
          <strong style={{ color: "#e5f0ff" }}>{fmtUSD(asking)}</strong>
        </div>
        <div style={{ textAlign: "center" }}>
          <div>{middleLabel}</div>
          <strong style={{ color: "#e5f0ff" }}>{fmtUSD(retail)}</strong>
        </div>
        <div style={{ width: 110, textAlign: "right" }}>
          <div>Profit</div>
          <strong style={{ color: "#22c55e" }}>{fmtUSD(profit)}</strong>
        </div>
      </div>

      <div style={{ position: "relative", height: 6 }}>
        <div
          style={{
            height: 6,
            width: "100%",
            borderRadius: 999,
            background:
              "linear-gradient(90deg, #e5f0ff 0%, #8b5cf6 50%, #22c55e 100%)",
            boxShadow: "0 0 14px rgba(139,92,246,.18) inset",
          }}
        />
        <Dot left={0} label="Asking" value={asking} color="#e5f0ff" />
        <Dot left={50} label={middleLabel} value={retail} color="#8b5cf6" />
        <Dot left={100} label="Profit" value={profit} color="#22c55e" />
      </div>
    </div>
  );
}

/** ---------- AI Recommendation (green → purple rim, subtle) ---------- */
function AISummaryCard({ action = "BUY", summary }) {
  return (
    <div
      style={{
        marginTop: 12,
        borderRadius: 14,
        padding: 1.25,
        background:
          "linear-gradient(95deg, rgba(20,224,199,0.95) 0%, rgba(139,92,246,0.95) 100%)",
        boxShadow:
          "0 16px 36px rgba(0,0,0,.45), 0 0 20px rgba(139,92,246,.14), 0 0 22px rgba(20,224,199,.12)",
      }}
    >
      <div
        style={{
          borderRadius: 12,
          background:
            "linear-gradient(180deg, rgba(10,14,22,.94) 0%, rgba(8,12,19,.94) 100%)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.05)",
          padding: 16,
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 28,
              padding: "0 12px",
              borderRadius: 999,
              border: "1px solid #14e0c7",
              color: "#a9f0ea",
              fontWeight: 800,
              letterSpacing: 0.4,
              background: "rgba(20,224,199,0.06)",
              boxShadow: "0 0 10px rgba(20,224,199,.25)",
            }}
          >
            {action}
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 800,
                color: "#E9EEFF",
                marginBottom: 6,
                letterSpacing: 0.2,
              }}
            >
              AI Recommendation
            </div>

            <div style={{ color: "#B6C1D4", marginBottom: 10 }}>{summary}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ---------- Profit Scenario ---------- */
function ProfitScenarioCard({ asking, retailOrAvg, dom }) {
  const FEES = 200;
  const QUICK_MARGIN = 0.03;
  const baseDom = typeof dom === "number" && dom > 0 ? dom : 16;

  const quickPrice = asking * (1 + QUICK_MARGIN);
  const quickProfit = quickPrice - asking - FEES;
  const quickDom = Math.max(3, Math.round(baseDom * 0.5));

  const retailPrice = retailOrAvg;
  const retailProfit = retailPrice - asking - FEES;
  const retailDom = Math.round(baseDom * 1.75);

  const Cell = ({ title, price, profit, days, tone = "green" }) => (
    <div
      style={{
        border: "1px solid rgba(255,255,255,.06)",
        borderRadius: 12,
        padding: 12,
        background:
          "linear-gradient(180deg, rgba(10,14,22,.88), rgba(8,12,19,.88))",
        boxShadow:
          "0 10px 30px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.04)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Pill tone={tone}>{title}</Pill>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ color: "#8ea0c6", fontSize: 12 }}>Price</div>
          <div style={{ fontWeight: 700, color: "#e5f0ff" }}>{fmtUSD(price)}</div>
        </div>
        <div>
          <div style={{ color: "#8ea0c6", fontSize: 12 }}>Gross</div>
          <div style={{ fontWeight: 700, color: "#e5f0ff" }}>{fmtUSD(profit)}</div>
        </div>
        <div>
          <div style={{ color: "#8ea0c6", fontSize: 12 }}>DOM</div>
          <div style={{ fontWeight: 700, color: "#e5f0ff" }}>{days} days</div>
        </div>
      </div>
      <div style={{ marginTop: 8, color: "#7483a1", fontSize: 12 }}>
        Assumes ${FEES.toLocaleString()} fees; quick flip margin {Math.round(QUICK_MARGIN * 100)}%.
      </div>
    </div>
  );

  return (
    <Card
      bordered
      style={{
        borderRadius: 12,
        borderColor: "rgba(255,255,255,.06)",
        background:
          "linear-gradient(180deg, rgba(9,13,21,.85), rgba(8,12,19,.85))",
        boxShadow: "0 12px 36px rgba(0,0,0,.45)",
      }}
      bodyStyle={{ padding: 16 }}
    >
      <div style={{ fontWeight: 700, marginBottom: 10, color: "#e5f0ff" }}>
        Profit Scenario
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Cell title="Quick Flip" tone="blue" price={quickPrice} profit={quickProfit} days={quickDom} />
        <Cell title="Retail Hold" tone="green" price={retailPrice} profit={retailProfit} days={retailDom} />
      </div>
    </Card>
  );
}

/** ---------- Market Comparison ---------- */
function MarketComparison({ overview, radius = "25 mi", onRadiusChange, lastUpdated = "Updated today", listingDate }) {
  const columns = [
    { title: "Window", dataIndex: "window", key: "window", render: (v) => <Text strong style={{ color:"#e5f0ff" }}>{v}</Text> },
    { title: "Units Sold", dataIndex: "sold", key: "sold", align: "right" },
    { title: "Active Listings", dataIndex: "active", key: "active", align: "right" },
    {
      title: "Avg Sold DOM",
      dataIndex: "dom",
      key: "dom",
      align: "right",
      render: (v) => (typeof v === "number" ? `${Math.round(v)} days` : "—"),
    },
  ];

  // Map API response to table rows
  const rows = [
    {
      key: "30",
      window: "Last 30 days",
      sold: overview?.sold_listings_count_30_days ?? "—",
      active: overview?.active_listings_count ?? "—",
      dom: overview?.sold_listings_avg_dom_30_days ?? "—",
    },
    {
      key: "60",
      window: "Last 60 days",
      sold: overview?.sold_listings_count_60_days ?? "—",
      active: overview?.active_listings_count ?? "—",
      dom: overview?.sold_listings_avg_dom_60_days ?? "—",
    },
    {
      key: "90",
      window: "Last 90 days",
      sold: overview?.sold_listings_count_90_days ?? "—",
      active: overview?.active_listings_count ?? "—",
      dom: overview?.sold_listings_avg_dom_90_days ?? "—",
    },
  ];
  const listed = fmtDate(listingDate);

  return (
    <Card
      bordered
      style={{
        borderRadius: 12,
        borderColor: "rgba(255,255,255,.06)",
        background: "linear-gradient(180deg, rgba(10,14,22,.85), rgba(8,12,19,.85))",
        boxShadow: "0 12px 36px rgba(0,0,0,.45)",
      }}
      bodyStyle={{ padding: 16 }}
      title={
        <div className="mc-header">
          <div className="mc-title">
            <span className="mc-h1">Market Comparison</span>
            <span className="mc-sub">
              {lastUpdated}
              {listed ? ` · Listed ${listed}` : ""}
            </span>
          </div>
          <div className="mc-controls">
            <Segmented
              options={[
                { label: "25 mi", value: "25 mi" },
                { label: "50 mi", value: "50 mi" },
              ]}
              value={radius}
              onChange={onRadiusChange}
              className="radius-segmented"
            />
          </div>
        </div>
      }
    >
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
        tableLayout="fixed"
        rowClassName={(_, idx) => (idx % 2 ? "table-alt-row" : "table-row")}
      />
      <style>{`
        .mc-header{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap;min-width:0}
        .mc-title{display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0}
        .mc-h1{font-weight:700;font-size:15px;color:#ffffff}
        .mc-sub{color:#a0a3b1;font-size:12px}
        .mc-pill-selected {
          background: linear-gradient(90deg, #3b82f6, #10b981) !important;
          color: #fff !important;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }
        .mc-pill {
          background: #1a1d2d;
          color: #a0a3b1;
          border-radius: 999px;
          border: none;
          font-weight: 700;
          font-size: 14px;
          transition: background 0.2s, color 0.2s;
        }
        .mc-controls .radius-segmented .ant-segmented-item {
          background: #1a1d2d;
          color: #a0a3b1;
          border-radius: 999px;
          border: 1px solid rgba(255, 255, 255, 0.05);
          font-weight: 700;
          font-size: 14px;
          transition: background 0.2s, color 0.2s;
        }
        .mc-controls .radius-segmented .ant-segmented-item-selected {
          background: linear-gradient(90deg, #3b82f6, #10b981);
          color: #fff;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }
        .ant-segmented-item-selected{background:#141726;color:#ffffff}
        .ant-table{background:transparent}
        .ant-table-thead > tr > th{
          background:#1a1d2d!important;
          color:#a0a3b1!important;
          border-bottom:1px solid rgba(255, 255, 255, 0.03)!important;
        }
        .ant-table-cell{
          background:transparent!important;
          color:#ffffff;
          border-bottom:1px solid rgba(255, 255, 255, 0.03)!important;
          padding:10px 12px!important;
        }
        .table-alt-row td{background:#1a1d2d!important}
      `}</style>
    </Card>
  );
}

/* -------------------- Mobile Metric Card -------------------- */
function MobileMetricCard({ icon, label, value, unit, subTag, tone = "indigo" }) {
  const palette = {
    indigo: { chip: "rgba(99,102,241,.14)", ring: "rgba(99,102,241,.45)", fg: "#a5b4fc" },
    blue:   { chip: "rgba(59,130,246,.14)", ring: "rgba(59,130,246,.45)", fg: "#93c5fd" },
    amber:  { chip: "rgba(245,158,11,.14)", ring: "rgba(245,158,11,.45)", fg: "#fcd34d" },
    teal:   { chip: "rgba(45,212,191,.14)", ring: "rgba(45,212,191,.45)", fg: "#5eead4" },
    gray:   { chip: "rgba(148,163,184,.12)", ring: "rgba(148,163,184,.35)", fg: "#cbd5e1" },
  }[tone];

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 14,
        background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
        border: "1px solid rgba(255,255,255,.06)",
        boxShadow: "0 10px 26px rgba(0,0,0,.35)",
        transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
      }}
      bodyStyle={{ padding: 14 }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = palette.ring)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,.06)")}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            background: palette.chip,
            boxShadow: `inset 0 0 0 1px ${palette.ring}`,
          }}
        >
          <div style={{ color: palette.fg, fontSize: 18, lineHeight: 1 }}>{icon}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600 }}>{label}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e5f0ff" }}>{value ?? "—"}</div>
            {unit ? <div style={{ color: "#9aa6b2", fontWeight: 700 }}>{unit}</div> : null}
          </div>
          {subTag}
        </div>
      </div>
    </Card>
  );
}

/* -------------------- Mobile Comps Component -------------------- */
function MobileCompsView({ listingId, subjectMiles, activeTab, setActiveTab, listing, overview, overviewLoading, overviewError, radius, setRadius }) {
  const [sortKey, setSortKey] = useState("price");
  const [sortDir, setSortDir] = useState("asc");
  const [usedSimilar, setUsedSimilar] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Use active listings directly from the listing object
  const activeComps = listing?.active_listings || [];
  const activeLoading = false; // No loading since it's from listing data
  const activeError = null;
  // Use sold listings directly from the listing object
  const soldComps = listing?.sold_listings || [];
  const soldLoading = false; // No loading since it's from listing data
  const soldError = null;

  // Get current comps based on active tab
  const currentComps = activeTab === "active_comps" ? activeComps : soldComps;
  const currentLoading = activeTab === "active_comps" ? activeLoading : soldLoading;

  // Calculate slider bounds from current comps data
  const allMiles = currentComps.map((c) => c.miles).filter((n) => typeof n === "number");
  const allDist = currentComps.map((c) => c.dist).filter((n) => typeof n === "number");
  const milesMin = allMiles.length ? Math.min(...allMiles) : 0;
  const milesMax = allMiles.length ? Math.max(...allMiles) : 200000;
  const distMin = allDist.length ? Math.floor(Math.min(...allDist)) : 0;
  const distMax = allDist.length ? Math.ceil(Math.max(...allDist)) : 100;

  const [mileageRange, setMileageRange] = useState([milesMin, milesMax]);
  const [distanceRange, setDistanceRange] = useState([distMin, Math.min(100, distMax)]); // Default to 100 miles
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState(null); // 'mileage-min', 'mileage-max', 'distance-min', 'distance-max'
  const [isWithin100Miles, setIsWithin100Miles] = useState(true); // Default to within 100 miles

  // Update ranges when data changes
  useEffect(() => {
    setMileageRange([milesMin, milesMax]);
    // Keep within 100 miles default unless user has changed it
    if (isWithin100Miles) {
      setDistanceRange([distMin, Math.min(100, distMax)]);
    }
  }, [milesMin, milesMax, distMin, distMax, isWithin100Miles]);

  // Filter and sort current comps
  const filtered = currentComps.filter((c) => {
    const m = typeof c.miles === "number" ? c.miles : null;
    const d = typeof c.dist === "number" ? c.dist : null;
    
    const mileagePass = m == null || (m >= mileageRange[0] && m <= mileageRange[1]);
    const distancePass = d == null || (d >= distanceRange[0] && d <= distanceRange[1]);
    
    return mileagePass && distancePass;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if (sortKey === "distance") {
      va = a.dist;
      vb = b.dist;
    } else if (sortKey === "price") {
      va = a.price;
      vb = b.price;
    } else if (sortKey === "sold_date") {
      va = a.last_seen_at_date ? new Date(a.last_seen_at_date).getTime() : null;
      vb = b.last_seen_at_date ? new Date(b.last_seen_at_date).getTime() : null;
    } else {
      va = a.miles;
      vb = b.miles;
    }
    
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const na = Number(va), nb = Number(vb);
    if (na === nb) return 0;
    return sortDir === "asc" ? (na > nb ? 1 : -1) : (na > nb ? -1 : 1);
  });

  // Calculate metrics from filtered set
  const count = filtered.length;
  const avgPrice = filtered.length ? Math.round(filtered.reduce((sum, c) => sum + (c.price || 0), 0) / filtered.length) : 0;
  const avgMiles = filtered.length ? Math.round(filtered.reduce((sum, c) => sum + (c.miles || 0), 0) / filtered.length) : 0;
  // Calculate avgDOM - only if there are items with valid dom_active values
  const avgDOM = (() => {
    const validDOMs = filtered.filter(c => c.dom_active != null && c.dom_active > 0);
    return validDOMs.length > 0 
      ? Math.round(validDOMs.reduce((sum, c) => sum + (c.dom_active || 0), 0) / validDOMs.length)
      : null;
  })();

  const toggleDir = () => setSortDir(prev => prev === "asc" ? "desc" : "asc");
  
  const useSimilarMileage = () => {
    if (usedSimilar) {
      setMileageRange([milesMin, milesMax]);
      setUsedSimilar(false);
    } else {
      const lo = Math.round(subjectMiles * 0.9);
      const hi = Math.round(subjectMiles * 1.1);
      setMileageRange([lo, hi]);
      setUsedSimilar(true);
    }
  };

  const useWithin100Miles = () => {
    if (isWithin100Miles) {
      // Turn off - use full range
      setIsWithin100Miles(false);
      setDistanceRange([distMin, distMax]);
    } else {
      // Turn on - use within 100 miles
      setIsWithin100Miles(true);
      setDistanceRange([distMin, Math.min(100, distMax)]);
    }
  };

  const clearFilters = () => {
    setUsedSimilar(false);
    setIsWithin100Miles(true); // Reset to within 100 miles default
    setMileageRange([milesMin, milesMax]);
    setDistanceRange([distMin, Math.min(100, distMax)]); // Reset to within 100 miles default
  };

  // Drag handlers for sliders
  const handleSliderDrag = (e, type) => {
    e.preventDefault();
    setIsDragging(true);
    setDragType(type);

    const sliderContainer = e.currentTarget.closest('[data-slider-container]');
    if (!sliderContainer) return;

    const handleMouseMove = (moveEvent) => {
      const rect = sliderContainer.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      if (type.includes('mileage')) {
        const value = Math.round((percentage / 100) * (milesMax - milesMin) + milesMin);
        if (type === 'mileage-min') {
          setMileageRange([Math.min(value, mileageRange[1] - 1000), mileageRange[1]]);
        } else {
          setMileageRange([mileageRange[0], Math.max(value, mileageRange[0] + 1000)]);
        }
      } else {
        const value = Math.round((percentage / 100) * (distMax - distMin) + distMin); // Use actual distance range
        if (type === 'distance-min') {
          const newRange = [Math.min(value, distanceRange[1] - 5), distanceRange[1]];
          setDistanceRange(newRange);
          // Update within 100 miles button state based on new range
          setIsWithin100Miles(newRange[0] === distMin && newRange[1] <= 100);
        } else {
          const newRange = [distanceRange[0], Math.max(value, distanceRange[0] + 5)];
          setDistanceRange(newRange);
          // Update within 100 miles button state based on new range
          setIsWithin100Miles(newRange[0] === distMin && newRange[1] <= 100);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDragType(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e, type) => {
    e.preventDefault();
    setIsDragging(true);
    setDragType(type);

    const sliderContainer = e.currentTarget.closest('[data-slider-container]');
    if (!sliderContainer) return;

    const handleTouchMove = (moveEvent) => {
      const rect = sliderContainer.getBoundingClientRect();
      const x = moveEvent.touches[0].clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      if (type.includes('mileage')) {
        const value = Math.round((percentage / 100) * (milesMax - milesMin) + milesMin);
        if (type === 'mileage-min') {
          setMileageRange([Math.min(value, mileageRange[1] - 1000), mileageRange[1]]);
        } else {
          setMileageRange([mileageRange[0], Math.max(value, mileageRange[0] + 1000)]);
        }
      } else {
        const value = Math.round((percentage / 100) * (distMax - distMin) + distMin); // Use actual distance range
        if (type === 'distance-min') {
          const newRange = [Math.min(value, distanceRange[1] - 5), distanceRange[1]];
          setDistanceRange(newRange);
          // Update within 100 miles button state based on new range
          setIsWithin100Miles(newRange[0] === distMin && newRange[1] <= 100);
        } else {
          const newRange = [distanceRange[0], Math.max(value, distanceRange[0] + 5)];
          setDistanceRange(newRange);
          // Update within 100 miles button state based on new range
          setIsWithin100Miles(newRange[0] === distMin && newRange[1] <= 100);
        }
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      setDragType(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Tab Selector */}
      <div style={{ 
        display: "flex", 
        gap: 6, 
        marginBottom: 20,
        flexWrap: "wrap"
      }}>
        <button
          onClick={() => setActiveTab("overview")}
          style={{
            flex: "1 1 100px",
            minWidth: "80px",
            padding: "10px 12px",
            borderRadius: 12,
            border: "none",
            background: activeTab === "overview" ? "linear-gradient(90deg, #6366f1, #22c55e)" : "rgba(255,255,255,.08)",
            color: "#ffffff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.3s ease",
            whiteSpace: "nowrap",
          }}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("active_comps")}
          style={{
            flex: "1 1 100px",
            minWidth: "80px",
            padding: "10px 12px",
            borderRadius: 12,
            border: "none",
            background: activeTab === "active_comps" ? "linear-gradient(90deg, #6366f1, #22c55e)" : "rgba(255,255,255,.08)",
            color: "#ffffff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.3s ease",
            whiteSpace: "nowrap",
          }}
        >
          Active Comps
        </button>
        <button
          onClick={() => setActiveTab("sold_comps")}
          style={{
            flex: "1 1 100px",
            minWidth: "80px",
            padding: "10px 12px",
            borderRadius: 12,
            border: "none",
            background: activeTab === "sold_comps" ? "linear-gradient(90deg, #6366f1, #22c55e)" : "rgba(255,255,255,.08)",
            color: "#ffffff",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.3s ease",
            whiteSpace: "nowrap",
          }}
        >
          Sold Comps
        </button>
      </div>

      {/* Overview Content */}
      {activeTab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <AISummaryCard
            action={listing.recommendation || "BUY"}
            summary={listing.recommendation_reason || "Strong private listing with solid profit and demand signals."}
          />

          <MarketComparison
            overview={overview}
            radius={radius}
            onRadiusChange={setRadius}
            listingDate={listing.listingDate || listing.listedAt || listing.postedAt || listing?.listing_date}
            lastUpdated={overviewLoading ? "Loading..." : overviewError ? "Error loading" : "Updated today"}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Space wrap>
              {listing.vdp_url ? (
                <Button
                  type="text"
                  href={listing.vdp_url}
                  target="_blank"
                  rel="noreferrer"
                  icon={<LinkOutlined />}
                  style={{ color: "#60a5fa" }}
                >
                  Original Listing
                </Button>
              ) : (
                <Text type="secondary" style={{ color: "var(--text-secondary)" }}>
                  No listing URL
                </Text>
              )}
            </Space>
          </div>
        </div>
      )}

      {/* Loading State */}
      {currentLoading && (
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          padding: "40px 20px",
          color: "#8ea0c6"
        }}>
          <Spin size="large" />
          <span style={{ marginLeft: 12 }}>Loading {activeTab === "active_comps" ? "active" : "sold"} comps...</span>
        </div>
      )}

      {/* Active Comps Content */}
      {!currentLoading && activeTab === "active_comps" && (
        <div style={{ padding: '20px 0' }}>
          {/* Filter and Sort Section */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            {/* Sort by */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 16,
              position: 'relative',
              gap: 8,
            }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                Sort by
              </div>
              <div style={{ position: 'relative' }}>
                <button 
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '8px 12px',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    minWidth: 80,
                  }}
                >
                  {sortKey === 'price' ? 'Price' : 'Mileage'}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                {showSortDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#1a1f3a',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    marginTop: 4,
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  }}>
                    {['Price', 'Mileage'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setSortKey(option.toLowerCase());
                          setShowSortDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          background: 'transparent',
                          border: 'none',
                          color: sortKey === option.toLowerCase() ? '#3b82f6' : '#ffffff',
                          padding: '8px 12px',
                          fontSize: 14,
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                        onMouseEnter={(e) => {
                          if (sortKey !== option.toLowerCase()) {
                            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (sortKey !== option.toLowerCase()) {
                            e.target.style.background = 'transparent';
                          }
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* ASC/DESC Arrow Buttons */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => setSortDir('asc')}
                  style={{
                    background: sortDir === 'asc' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '8px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 36,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 3L9 9H3L6 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  onClick={() => setSortDir('desc')}
                  style={{
                    background: sortDir === 'desc' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '8px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 36,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 9L3 3H9L6 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Distance Section */}
            <div style={{ marginBottom: 16 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
                <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                  Distance
                </div>
                <div style={{ color: '#ffffff', fontSize: 14 }}>
                  {distanceRange[0]}–{distanceRange[1]} mi
                </div>
              </div>
              
              {/* Distance Slider */}
              <div 
                data-slider-container
                style={{
                  height: 4,
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 2,
                  position: 'relative',
                  marginBottom: 12,
                }}
              >
                {/* Filled Section */}
                <div style={{
                  position: 'absolute',
                  left: `${((distanceRange[0] - distMin) / (distMax - distMin)) * 100}%`,
                  top: 0,
                  height: '100%',
                  width: `${((distanceRange[1] - distanceRange[0]) / (distMax - distMin)) * 100}%`,
                  background: '#60a5fa',
                  borderRadius: 2,
                }}></div>
                {/* Min Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'distance-min')}
                  onTouchStart={(e) => handleTouchStart(e, 'distance-min')}
                  style={{
                    position: 'absolute',
                    left: `${((distanceRange[0] - distMin) / (distMax - distMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
                {/* Max Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'distance-max')}
                  onTouchStart={(e) => handleTouchStart(e, 'distance-max')}
                  style={{
                    position: 'absolute',
                    left: `${((distanceRange[1] - distMin) / (distMax - distMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
              </div>

              {/* Within 100 Miles Button */}
              <button 
                onClick={useWithin100Miles}
                style={{
                  background: isWithin100Miles 
                    ? 'linear-gradient(135deg, #60a5fa, #3b82f6)' 
                    : 'transparent',
                  border: '1px solid #60a5fa',
                  borderRadius: 20,
                  color: '#ffffff',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: isWithin100Miles ? 600 : 500,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: isWithin100Miles 
                    ? '0 4px 12px rgba(96, 165, 250, 0.3)' 
                    : 'none',
                  marginTop: 12,
                }}
              >
                {isWithin100Miles ? '✓ Within 100 miles' : 'Within 100 miles'}
              </button>
            </div>

            {/* Mileage Section */}
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
                <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                  Mileage
                </div>
                <div style={{ color: '#ffffff', fontSize: 14 }}>
                  {mileageRange[0].toLocaleString()}–{mileageRange[1].toLocaleString()} mi
                </div>
              </div>
              
              {/* Mileage Slider */}
              <div 
                data-slider-container
                style={{
                  height: 4,
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 2,
                  position: 'relative',
                  marginBottom: 12,
                }}
              >
                {/* Filled Section */}
                <div style={{
                  position: 'absolute',
                  left: `${((mileageRange[0] - milesMin) / (milesMax - milesMin)) * 100}%`,
                  top: 0,
                  height: '100%',
                  width: `${((mileageRange[1] - mileageRange[0]) / (milesMax - milesMin)) * 100}%`,
                  background: '#60a5fa',
                  borderRadius: 2,
                }}></div>
                {/* Min Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'mileage-min')}
                  onTouchStart={(e) => handleTouchStart(e, 'mileage-min')}
                  style={{
                    position: 'absolute',
                    left: `${((mileageRange[0] - milesMin) / (milesMax - milesMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
                {/* Max Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'mileage-max')}
                  onTouchStart={(e) => handleTouchStart(e, 'mileage-max')}
                  style={{
                    position: 'absolute',
                    left: `${((mileageRange[1] - milesMin) / (milesMax - milesMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
              </div>

              {/* Similar Mileage Button */}
              <button 
                onClick={useSimilarMileage}
                style={{
                  background: usedSimilar 
                    ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                    : 'transparent',
                  border: '1px solid #a855f7',
                  borderRadius: 20,
                  color: '#ffffff',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: usedSimilar ? 600 : 500,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: usedSimilar 
                    ? '0 4px 12px rgba(168, 85, 247, 0.3)' 
                    : 'none',
                }}
              >
                {usedSimilar ? '✓ Similar mileage ±10%' : 'Similar mileage ±10%'}
              </button>
            </div>
          </div>

          {/* Summary Cards for Active Tab - Compact Mobile Design */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {/* Top Row - Active Listings and Avg Mileage */}
            <div style={{
              display: 'flex',
              gap: 6,
            }}>
              {/* Active Listings */}
              <div style={{
                flex: 1,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                borderRadius: 8,
                padding: 8,
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid rgba(59, 130, 246, 0.2)',
                minHeight: 60,
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)',
                }}>
                  <AppstoreOutlined style={{ fontSize: 12, color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#93c5fd', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                    ACTIVE<br/>LISTINGS
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                    {count}
                  </div>
                </div>
              </div>

              {/* Avg Mileage */}
              <div style={{
                flex: 1,
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                borderRadius: 8,
                padding: 8,
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid rgba(245, 158, 11, 0.2)',
                minHeight: 60,
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)',
                }}>
                  <DashboardOutlined style={{ fontSize: 12, color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fcd34d', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                    AVG<br/>MILEAGE
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                    {avgMiles ? `${Math.round(avgMiles / 1000)}k` : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row - Avg Price (full width) */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
              borderRadius: 8,
              padding: 8,
              border: '1px solid rgba(34, 197, 94, 0.2)',
              boxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 60,
            }}>
              <div style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(34, 197, 94, 0.3)',
              }}>
                <DollarOutlined style={{ fontSize: 12, color: '#ffffff' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#86efac', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                  AVG<br/>PRICE
                </div>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1 }}>
                  ${avgPrice ? avgPrice.toLocaleString() : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Active Listings */}
          <div style={{ marginTop: 24 }}>
            {sorted.length > 0 ? (
              sorted.map((c, index) => (
              <div
                key={c.vdp_url || `${c.heading}-${c.price}-${c.miles}`}
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => {
                  if (c.vdp_url) {
                    window.open(c.vdp_url, '_blank');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  width: '100%',
                }}>
                  {/* Vehicle Image */}
                  <div
                    style={{
                      width: 80,
                      height: 60,
                      borderRadius: 8,
                      overflow: 'hidden',
                      backgroundColor: '#1a1a1a',
                      position: 'relative',
                      flexShrink: 0,
                    }}
                  >
                    {c.media?.photo_links_cached?.[0] ? (
                      <img
                        src={c.media.photo_links_cached[0]}
                        alt={c.heading || `${c.year} ${c.make} ${c.model}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        fontSize: 24,
                      }}>
                        <AppstoreOutlined />
                      </div>
                    )}
                  </div>
                  
                  {/* Vehicle Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 700,
                      marginBottom: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      textTransform: 'uppercase',
                    }}>
                      {c.heading || `${c.year} {c.make} {c.model} ${c.trim}`}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 4,
                    }}>
                      <div style={{
                        color: '#ffffff',
                        fontSize: 16,
                        fontWeight: 700,
                      }}>
                        ${c.price?.toLocaleString() || 'N/A'}
                      </div>
                      <div style={{
                        color: '#60a5fa',
                        fontSize: 14,
                        fontWeight: 600,
                      }}>
                        {c.miles?.toLocaleString() || 'N/A'} mi
                      </div>
                    </div>
                    <div style={{
                      color: '#9ca3af',
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}>
                      {c.dealer?.name || c.dealer_name || 'Unknown Dealer'}
                      {typeof c.dist === "number" && (
                        <span style={{ marginLeft: 8, color: '#93c5fd' }}>
                          · {c.dist.toFixed(1)} mi
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div style={{
                    color: '#6b7280',
                    fontSize: 16,
                    flexShrink: 0,
                  }}>
                    <RightOutlined />
                  </div>
                </div>
              </div>
              ))
            ) : (
              <div style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#8ea0c6",
                background: "linear-gradient(180deg, #0f1422, #0b101a)",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,.06)",
              }}>
                <div style={{ fontSize: 16, marginBottom: 8 }}>No Active Listings</div>
                <div style={{ fontSize: 14 }}>No active comparable listings found for this vehicle</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sold Comps Content */}
      {!currentLoading && activeTab === "sold_comps" && (
        <div style={{ padding: '20px 0' }}>
          {/* Filter and Sort Section */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            {/* Sort by */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 16,
              position: 'relative',
              gap: 8,
            }}>
              <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                Sort by
              </div>
              <div style={{ position: 'relative' }}>
                <button 
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '8px 12px',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    minWidth: 80,
                  }}
                >
                  {sortKey === 'price' ? 'Price' : sortKey === 'sold_date' ? 'Sold Date' : 'Mileage'}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                {showSortDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#1a1f3a',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    marginTop: 4,
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  }}>
                    {['Price', 'Mileage', 'Sold Date'].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setSortKey(option.toLowerCase().replace(' ', '_'));
                          setShowSortDropdown(false);
                        }}
                        style={{
                          width: '100%',
                          background: 'transparent',
                          border: 'none',
                          color: sortKey === option.toLowerCase().replace(' ', '_') ? '#3b82f6' : '#ffffff',
                          padding: '8px 12px',
                          fontSize: 14,
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                        onMouseEnter={(e) => {
                          if (sortKey !== option.toLowerCase().replace(' ', '_')) {
                            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (sortKey !== option.toLowerCase().replace(' ', '_')) {
                            e.target.style.background = 'transparent';
                          }
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* ASC/DESC Arrow Buttons */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => setSortDir('asc')}
                  style={{
                    background: sortDir === 'asc' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '8px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 36,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 3L9 9H3L6 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  onClick={() => setSortDir('desc')}
                  style={{
                    background: sortDir === 'desc' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 6,
                    color: '#ffffff',
                    padding: '8px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 36,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 9L3 3H9L6 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Distance Section */}
            <div style={{ marginBottom: 16 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
                <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                  Distance
                </div>
                <div style={{ color: '#ffffff', fontSize: 14 }}>
                  {distanceRange[0]}–{distanceRange[1]} mi
                </div>
              </div>
              
              {/* Distance Slider */}
              <div 
                data-slider-container
                style={{
                  height: 4,
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 2,
                  position: 'relative',
                  marginBottom: 12,
                }}
              >
                {/* Filled Section */}
                <div style={{
                  position: 'absolute',
                  left: `${((distanceRange[0] - distMin) / (distMax - distMin)) * 100}%`,
                  top: 0,
                  height: '100%',
                  width: `${((distanceRange[1] - distanceRange[0]) / (distMax - distMin)) * 100}%`,
                  background: '#60a5fa',
                  borderRadius: 2,
                }}></div>
                {/* Min Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'distance-min')}
                  onTouchStart={(e) => handleTouchStart(e, 'distance-min')}
                  style={{
                    position: 'absolute',
                    left: `${((distanceRange[0] - distMin) / (distMax - distMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
                {/* Max Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'distance-max')}
                  onTouchStart={(e) => handleTouchStart(e, 'distance-max')}
                  style={{
                    position: 'absolute',
                    left: `${((distanceRange[1] - distMin) / (distMax - distMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
              </div>

              {/* Within 100 Miles Button */}
              <button 
                onClick={useWithin100Miles}
                style={{
                  background: isWithin100Miles 
                    ? 'linear-gradient(135deg, #60a5fa, #3b82f6)' 
                    : 'transparent',
                  border: '1px solid #60a5fa',
                  borderRadius: 20,
                  color: '#ffffff',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: isWithin100Miles ? 600 : 500,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: isWithin100Miles 
                    ? '0 4px 12px rgba(96, 165, 250, 0.3)' 
                    : 'none',
                  marginTop: 12,
                }}
              >
                {isWithin100Miles ? '✓ Within 100 miles' : 'Within 100 miles'}
              </button>
            </div>

            {/* Mileage Section */}
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
                <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                  Mileage
                </div>
                <div style={{ color: '#ffffff', fontSize: 14 }}>
                  {mileageRange[0].toLocaleString()}–{mileageRange[1].toLocaleString()} mi
                </div>
              </div>
              
              {/* Mileage Slider */}
              <div 
                data-slider-container
                style={{
                  height: 4,
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 2,
                  position: 'relative',
                  marginBottom: 12,
                }}
              >
                {/* Filled Section */}
                <div style={{
                  position: 'absolute',
                  left: `${((mileageRange[0] - milesMin) / (milesMax - milesMin)) * 100}%`,
                  top: 0,
                  height: '100%',
                  width: `${((mileageRange[1] - mileageRange[0]) / (milesMax - milesMin)) * 100}%`,
                  background: '#60a5fa',
                  borderRadius: 2,
                }}></div>
                {/* Min Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'mileage-min')}
                  onTouchStart={(e) => handleTouchStart(e, 'mileage-min')}
                  style={{
                    position: 'absolute',
                    left: `${((mileageRange[0] - milesMin) / (milesMax - milesMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
                {/* Max Circle */}
                <div 
                  onMouseDown={(e) => handleSliderDrag(e, 'mileage-max')}
                  onTouchStart={(e) => handleTouchStart(e, 'mileage-max')}
                  style={{
                    position: 'absolute',
                    left: `${((mileageRange[1] - milesMin) / (milesMax - milesMin)) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 16,
                    height: 16,
                    background: '#ffffff',
                    borderRadius: '50%',
                    border: '2px solid #60a5fa',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                ></div>
              </div>

              {/* Similar Mileage Button */}
              <button 
                onClick={useSimilarMileage}
                style={{
                  background: usedSimilar 
                    ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                    : 'transparent',
                  border: '1px solid #a855f7',
                  borderRadius: 20,
                  color: '#ffffff',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: usedSimilar ? 600 : 500,
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: usedSimilar 
                    ? '0 4px 12px rgba(168, 85, 247, 0.3)' 
                    : 'none',
                }}
              >
                {usedSimilar ? '✓ Similar mileage ±10%' : 'Similar mileage ±10%'}
              </button>
            </div>
          </div>
          
          {/* Summary Cards for Sold Tab - Compact Mobile Design */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {/* Top Row - Sold Listings and Avg Mileage */}
            <div style={{
              display: 'flex',
              gap: 6,
            }}>
              {/* Sold Listings */}
              <div style={{
                flex: 1,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                borderRadius: 8,
                padding: 8,
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid rgba(59, 130, 246, 0.2)',
                minHeight: 60,
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)',
                }}>
                  <AppstoreOutlined style={{ fontSize: 12, color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#93c5fd', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                    SOLD<br/>LISTINGS
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                    {count}
                  </div>
                </div>
              </div>

              {/* Avg Mileage */}
              <div style={{
                flex: 1,
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                borderRadius: 8,
                padding: 8,
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid rgba(245, 158, 11, 0.2)',
                minHeight: 60,
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)',
                }}>
                  <DashboardOutlined style={{ fontSize: 12, color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#fcd34d', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                    AVG<br/>MILEAGE
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                    {avgMiles ? `${Math.round(avgMiles / 1000)}k` : '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row - Avg Price and Avg DOM */}
            <div style={{
              display: 'flex',
              gap: 6,
            }}>
              {/* Avg Price */}
              <div style={{
                flex: 1,
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                borderRadius: 8,
                padding: 8,
                border: '1px solid rgba(34, 197, 94, 0.2)',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 60,
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(34, 197, 94, 0.3)',
                }}>
                  <DollarOutlined style={{ fontSize: 12, color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#86efac', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                    AVG<br/>PRICE
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                    ${avgPrice ? avgPrice.toLocaleString() : '—'}
                  </div>
                </div>
              </div>

              {/* Avg DOM */}
              <div style={{
                flex: 1,
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)',
                borderRadius: 8,
                padding: 8,
                border: '1px solid rgba(168, 85, 247, 0.2)',
                boxShadow: '0 2px 8px rgba(168, 85, 247, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minHeight: 60,
              }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 6px rgba(168, 85, 247, 0.3)',
                }}>
                  <ClockCircleOutlined style={{ fontSize: 12, color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#c4b5fd', fontSize: 8, fontWeight: 600, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1 }}>
                    AVG<br/>DOM
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1 }}>
                    {avgDOM != null ? `${avgDOM}d` : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sold Listings */}
          <div style={{ marginTop: 24 }}>
            {sorted.length > 0 ? (
              sorted.map((c, index) => (
              <div
                key={c.vdp_url || `${c.heading}-${c.price}-${c.miles}`}
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 12,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => {
                  if (c.vdp_url) {
                    window.open(c.vdp_url, '_blank');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  width: '100%',
                }}>
                  {/* Vehicle Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 700,
                      marginBottom: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      textTransform: 'uppercase',
                    }}>
                      {c.heading || `${c.year} {c.make} {c.model} ${c.trim}`}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 4,
                    }}>
                      <div style={{
                        color: '#ffffff',
                        fontSize: 16,
                        fontWeight: 700,
                      }}>
                        ${c.price?.toLocaleString() || 'N/A'}
                      </div>
                      <div style={{
                        color: '#60a5fa',
                        fontSize: 14,
                        fontWeight: 600,
                      }}>
                        {c.miles?.toLocaleString() || 'N/A'} mi
                      </div>
                      {c.dom_active && (
                        <div style={{
                          background: 'rgba(168, 85, 247, 0.2)',
                          border: '1px solid #a855f7',
                          borderRadius: 12,
                          padding: '2px 8px',
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#c4b5fd',
                          whiteSpace: 'nowrap',
                        }}>
                          {c.dom_active} days
                        </div>
                      )}
                    </div>
                    <div style={{
                      color: '#9ca3af',
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.dealer?.name || c.dealer_name || 'Unknown Dealer'}
                      </span>
                      {typeof c.dist === "number" && (
                        <span style={{ color: '#93c5fd', whiteSpace: 'nowrap' }}>
                          · {c.dist.toFixed(1)} mi
                        </span>
                      )}
                      {activeTab === "sold_comps" && c.last_seen_at_date && (
                        <span style={{
                          background: 'rgba(34, 197, 94, 0.1)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          borderRadius: 12,
                          padding: '2px 8px',
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#22c55e',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}>
                          Sold {c.last_seen_at_date ? new Date(c.last_seen_at_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div style={{
                    color: '#6b7280',
                    fontSize: 16,
                    flexShrink: 0,
                  }}>
                    <RightOutlined />
                  </div>
                </div>
              </div>
              ))
            ) : (
              <div style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "#8ea0c6",
                background: "linear-gradient(180deg, #0f1422, #0b101a)",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,.06)",
              }}>
                <div style={{ fontSize: 16, marginBottom: 8 }}>No Sold Listings</div>
                <div style={{ fontSize: 14 }}>No sold comparable listings found for this vehicle</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- main drawer -------------------- */
export default function InsightsDrawer({ open, onClose, listing, onWatchlist, drawerWidth, placement = 'right' }) {
  if (!listing) return null;

  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const {
    year, make, model, trim, version,
    city, state,
    askingPrice, retailPrice, marketAvg, estProfit,
    dom, unitsSold30d, activeListings,
    grade, listingUrl, demand = "High",
    ai = {}, listingDate, listedAt, postedAt,
    title,
  } = listing;

  const computedMarketAvg =
    typeof marketAvg === "number" ? marketAvg : Math.round((askingPrice + retailPrice) / 2);

  const [radius, setRadius] = React.useState("25 mi");
  const numericRadius = Number(radius.replace(/\D/g, "")) || 25;
  const { data: overview, loading: overviewLoading, error: overviewError } = useOverview(listing?.id || listing?.listingId || listing?.vin, numericRadius);

  // Always default to "overview" tab when drawer opens
  const [activeTab, setActiveTab] = React.useState("overview");
  const [marketScope, setMarketScope] = React.useState('my-state');
  const [dealerState, setDealerState] = React.useState(null);
  
  React.useEffect(() => {
    if (open) setActiveTab("overview");
  }, [open]);

  // Fetch dealer state from user details
  React.useEffect(() => {
    const fetchDealerState = async () => {
      try {
        const userDetails = await fetchUserDetails();
        const state = userDetails?.dealership_state || null;
        setDealerState(state);
      } catch (error) {
        console.error('Error fetching dealer state:', error);
      }
    };
    fetchDealerState();
  }, []);

  const listedWhen = fmtDateWithTime(listingDate || listedAt || postedAt || listing?.listing_date);
  const retailOrAvg = typeof retailPrice === "number" ? retailPrice : computedMarketAvg;

  // Check if this is a Private Listings entry
  // Recommended Listings always have a valid retail or retailPrice field from the API
  // Private Listings don't have a valid retail/retailPrice, but may have active_listings array
  // Check both listing.retail and retailPrice (from destructured listing)
  const hasRetailPrice = (
    (typeof listing?.retail === "number" && listing.retail > 0) ||
    (typeof retailPrice === "number" && retailPrice > 0)
  );
  
  const isPrivateListing = (
    !hasRetailPrice &&
    Array.isArray(listing?.active_listings) && 
    listing.active_listings.length > 0
  );
  
  // For Private Listings: Use market_avg from data if available, otherwise calculate from similar mileage listings
  // For Recommended Listings: Use retail price
  const calculatePrivateListingsMarketAvg = () => {
    if (!isPrivateListing) return null;
    
    // First, check if market_avg is already in the listing data
    if (typeof listing?.market_avg === "number" && listing.market_avg > 0) {
      return listing.market_avg;
    }
    if (typeof marketAvg === "number" && marketAvg > 0) {
      return marketAvg;
    }
    
    // If not available, calculate from similar mileage listings
    const activeListings = listing?.active_listings || [];
    const subjectMiles = listing?.miles || listing?.mileage || 0;
    
    if (!activeListings.length || !subjectMiles) return null;
    
    // Filter listings within ±10% of subject mileage
    const similarMileageListings = activeListings.filter(listing => {
      const listingMileage = listing.miles || listing.mileage || 0;
      if (!listingMileage) return false;
      const mileageDiff = Math.abs(listingMileage - subjectMiles) / subjectMiles;
      return mileageDiff <= 0.1; // ±10% mileage
    });
    
    // Calculate average price from similar mileage listings
    const prices = similarMileageListings
      .map(listing => listing.price || 0)
      .filter(price => price > 0);
    
    if (prices.length === 0) return null;
    const avgPrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    return Math.round(avgPrice);
  };
  
  // For Recommended Listings: use retail price (from listing.retail or retailPrice)
  // For Private Listings: use market_avg (from data or calculated)
  const finalRetailOrAvg = isPrivateListing 
    ? (calculatePrivateListingsMarketAvg() ?? retailOrAvg)
    : (listing?.retail || retailPrice || retailOrAvg);
  const middleLabel = isPrivateListing ? "Market Avg" : "Retail";
  
  // Calculate profit: For Private Listings, profit = market avg - asking
  // For Recommended Listings, use existing profit calculation
  const calculatedProfit = isPrivateListing 
    ? (typeof finalRetailOrAvg === "number" && finalRetailOrAvg > 0 
        ? finalRetailOrAvg - Number(listing.asking_price || askingPrice || 0)
        : Number(listing.profit || estProfit || 0))
    : Number(listing.profit || estProfit || 0);

  // Default width calculation if drawerWidth prop is not provided
  // Match Private Listings drawer width: 850px for large screens, 750px for medium, 100% for mobile
  const defaultWidth = window.innerWidth <= 768 ? '100%' : window.innerWidth > 1400 ? 850 : 750;
  const drawerWidthValue = drawerWidth !== undefined ? drawerWidth : defaultWidth;

  // Set height for top placement
  const drawerHeight = placement === 'top' ? (window.innerHeight * 0.9) : undefined;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={drawerWidthValue}
      height={drawerHeight}
      placement={placement}
      className="insights-drawer insights-drawer-mobile"
      closable={false}
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: "block",
                  color: "#EAF1FF",
                  fontWeight: 900,
                  fontSize: 18,
                }}
              >
                {title || `${year} ${make} ${model}${version ? ` · ${version}` : ""}`}
              </span>
              <span style={{ color: "#9EB0CB", fontSize: 12 }}>
                Mileage: {listing?.miles?.toLocaleString() ?? "—"} • Listed: {listedWhen ?? "—"}
              </span>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              position: isMobile ? "fixed" : "absolute",
              right: isMobile ? "20px" : "16px",
              top: isMobile ? "20px" : "50%",
              transform: isMobile ? "none" : "translateY(-50%)",
              background: 'none',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: 24,
              fontWeight: 400,
              padding: 0,
              width: 'auto',
              height: 'auto',
              zIndex: 9999,
            }}
          >
            ×
          </button>
        </div>
      }
      styles={{
        header: {
          background: "linear-gradient(180deg, rgba(15,18,28,1), rgba(11,15,24,1))",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          padding: "14px 16px",
        },
        body: {
          background: "linear-gradient(180deg, rgba(15,18,28,.96), rgba(9,12,20,.96))",
          padding: 16,
          overflowX: "hidden",
        },
        mask: { backdropFilter: "blur(2px)" },
        content: {
          borderRadius: 16,
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
          background: "transparent",
        },
      }}
    >
      <PriceRail
        asking={Number(listing.asking_price || askingPrice || 0)}
        retail={Number(finalRetailOrAvg || 0)}
        profit={calculatedProfit}
        middleLabel={middleLabel}
      />

      <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="insights-tabs"
          tabBarExtraContent={{
            right: (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Typography.Text style={{ 
                  color: '#8ea0c6', 
                  fontSize: 12, 
                  fontWeight: 500,
                  whiteSpace: 'nowrap'
                }}>
                  Market Scope:
                </Typography.Text>
                <Segmented
                  value={marketScope}
                  onChange={setMarketScope}
                  options={[
                    { label: dealerState ? `My State (${dealerState})` : 'My State', value: 'my-state' },
                    { label: 'All States', value: 'all-states' }
                  ]}
                  className="market-scope-segmented"
                />
              </div>
            )
          }}
          items={[
            {
              key: "overview",
              label: (
                <span>
                  <LineChartOutlined /> Overview
                </span>
              ),
              children: (() => {
                // Transform listing data to match Search Insights Overview component format
                // Use active_listings and sold_listings from overview or listing for all metrics
                const soldListings = overview?.sold_listings || overview?.recent_sales || listing.sold_listings || [];
                const activeListings = overview?.active_listings || listing.active_listings || [];
                const soldPrices = soldListings.map((s) => s.price || s.sold_price).filter((p) => p && p > 0);
                const soldDoms = soldListings.map((s) => s.dom_active || s.dom).filter((d) => d != null && d > 0);
                const marketAvgFromSold = soldPrices.length > 0 ? soldPrices.reduce((a, b) => a + b, 0) / soldPrices.length : null;
                const avgDomFromSold = soldDoms.length > 0 ? soldDoms.reduce((a, b) => a + b, 0) / soldDoms.length : null;
                const transformedData = {
                  vehicle_info: {
                    year: listing.year || year,
                    make: listing.make || make,
                    model: listing.model || model,
                    trim: listing.trim || trim,
                    version: listing.version || version || listing.trim || trim,
                    miles: listing.miles,
                    mileage: listing.miles,
                    vin: listing.vin || listing.id || listing.listingId,
                  },
                  market_analysis: {
                    recent_sales: soldListings,
                    active_listings: activeListings,
                    market_metrics: {
                      market_avg_price: marketAvgFromSold ?? overview?.market_avg_price ?? retailPrice ?? marketAvg ?? computedMarketAvg,
                      avg_days_on_market: avgDomFromSold ?? listing.sold_dom ?? overview?.avg_days_on_market ?? dom ?? overview?.sold_listings_avg_dom_30_days ?? null,
                      sold_count: soldListings.length,
                      active_count: activeListings.length,
                    },
                    recommendation: {
                      action: listing.recommendation || "BUY",
                    },
                  },
                  pricing_info: {
                    marketcheck_price: listing.asking_price || askingPrice,
                    hooptie_price: listing.asking_price || askingPrice,
                    retail_ceiling: listing.retail || retailPrice,
                    confidence: "medium",
                  },
                  search_metadata: {
                    fetched_at: listing.listing_date || listingDate || listedAt || postedAt,
                    mileage: listing.miles,
                    miles: listing.miles,
                    vin: listing.vin || listing.id || listing.listingId,
                  },
                };

                return (
                  <MarketAnalysis 
                    data={transformedData} 
                    activeTab={activeTab}
                    marketScope={marketScope}
                    dealerState={dealerState}
                  />
                );
              })(),
            },
            {
              key: "active_comps",
              label: "Active Comps",
              children: (() => {
                // Transform listing data to match Search Insights component format
                const transformedData = {
                  vehicle_info: {
                    year: listing.year || year,
                    make: listing.make || make,
                    model: listing.model || model,
                    trim: listing.trim || trim,
                    version: listing.version || version || listing.trim || trim,
                    miles: listing.miles,
                    mileage: listing.miles,
                    vin: listing.vin || listing.id || listing.listingId,
                  },
                  market_analysis: {
                    recent_sales: overview?.sold_listings || overview?.recent_sales || listing.sold_listings || [],
                    active_listings: overview?.active_listings || listing.active_listings || [],
                  },
                  active_listings: overview?.active_listings || listing.active_listings || [],
                };

                return (
                  <ActiveCompsContent 
                    data={transformedData}
                    marketScope={marketScope}
                    dealerState={dealerState}
                  />
                );
              })(),
            },
            {
              key: "sold_comps",
              label: "Sold Comps",
              children: (() => {
                // Transform listing data to match Search Insights component format
                const transformedData = {
                  vehicle_info: {
                    year: listing.year || year,
                    make: listing.make || make,
                    model: listing.model || model,
                    trim: listing.trim || trim,
                    version: listing.version || version || listing.trim || trim,
                    miles: listing.miles,
                    mileage: listing.miles,
                    vin: listing.vin || listing.id || listing.listingId,
                  },
                  market_analysis: {
                    recent_sales: overview?.sold_listings || overview?.recent_sales || listing.sold_listings || [],
                    active_listings: overview?.active_listings || listing.active_listings || [],
                  },
                  recent_sales: overview?.sold_listings || overview?.recent_sales || listing.sold_listings || [],
                };

                return (
                  <SoldCompsContent 
                    data={transformedData}
                    marketScope={marketScope}
                    dealerState={dealerState}
                  />
                );
              })(),
            },
          ]}
        />
      
      {/* Tabs cosmetics */}
      <style>{`
        .insights-tabs .ant-tabs-nav{ margin:0 0 8px 0; }
        .insights-tabs .ant-tabs-tab{ color:#a0a3b1; }
        .insights-tabs .ant-tabs-ink-bar{
          background: linear-gradient(90deg, #10b981, #22d3ee); /* green→cyan */
          height:2px;
        }
        .insights-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn{
          color:#ffffff;
          text-shadow: 0 0 14px rgba(34, 211, 238, 0.5);
        }
        
        .market-scope-segmented {
          background: transparent !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          border-radius: 8px !important;
          padding: 2px !important;
        }
        
        .market-scope-segmented .ant-segmented-item {
          background: transparent !important;
          color: #8ea0c6 !important;
          border: none !important;
          border-radius: 6px !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          padding: 6px 16px !important;
          transition: all 0.2s ease !important;
        }
        
        .market-scope-segmented .ant-segmented-item-selected {
          background: #9146FF !important;
          color: #ffffff !important;
          box-shadow: 0 2px 8px rgba(145, 70, 255, 0.4) !important;
        }
        
        .market-scope-segmented .ant-segmented-item:hover:not(.ant-segmented-item-selected) {
          background: rgba(255, 255, 255, 0.05) !important;
        }
        .insights-drawer .ant-drawer-close,
        .insights-drawer .ant-drawer-close .anticon {
          color: #ffffff !important;
        }
        
        /* Force drawer header colors */
        .insights-drawer .ant-drawer-header {
          background: linear-gradient(180deg, rgba(15,18,28,1), rgba(11,15,24,1)) !important;
          border-bottom: 1px solid rgba(255,255,255,.06) !important;
        }
        
        .insights-drawer .ant-drawer-title {
          color: #EAF1FF !important;
        }
        
        .insights-drawer .ant-drawer-title span {
          color: #EAF1FF !important;
        }
        
        .insights-drawer .ant-drawer-title div span {
          color: #9EB0CB !important;
        }
        
        /* Force drawer body colors */
        .insights-drawer .ant-drawer-body {
          background: linear-gradient(180deg, rgba(15,18,28,.96), rgba(9,12,20,.96)) !important;
          color: #ffffff !important;
        }
        
        /* Override drawer backgrounds */
        .insights-drawer .ant-drawer-content {
          background: #0d0f1a !important;
        }
        
        .insights-drawer .ant-drawer-content-wrapper {
          background: #0d0f1a !important;
        }
      `}</style>
    </Drawer>
  );
}
